import { Injectable } from '@angular/core';
import { merge } from 'lodash-es';
import { TokenInformationModel } from 'src/app/shared/models/response/auth-service.model';
import { HierarchyComplementaryInformationModel, UserDataModel } from 'src/app/shared/models/user-data.model';
import { Constants } from '../constants/constants';
import { LocalStorageAgentData, LocalStorageData, SessionInformation } from './data-store-models';

@Injectable({
  providedIn: 'root',
})
export class DataStoreService {
  public serivceContractBinAndSipag: number[] = [101, 102, 103];
  public serivceContractBinAndSipagTecnology: number[] = [101, 102, 103, 125];

  constructor() {}

  public getUserData(): UserDataModel {
    const localStorageItem = localStorage.getItem(Constants.ACCREDITATION_USER_DATA_KEY);

    if (localStorageItem) {
      const parsed: TokenInformationModel = JSON.parse(localStorageItem);

      if (parsed && parsed.inf) {
        const userData: UserDataModel = {
          hierarchyComplementaryInformation: parsed.inf.hierarchyComplementaryInformation,
          cpfCnpj: parsed.inf.cpfCnpj,
          userType: parsed.inf.userType,
          channel: parsed.inf.channel,
          institution: parsed.inf.institution,
          channelType: parsed.inf.channelType,
          serviceContract: parsed.inf.serviceContract,
        };
        return userData;
      }
    }
  }

  public setUserData(userData: UserDataModel) {
    const localStorageItem = localStorage.getItem(Constants.ACCREDITATION_USER_DATA_KEY);

    if (localStorageItem) {
      const parsed: TokenInformationModel = JSON.parse(localStorageItem);
      if (parsed && parsed.inf) {
        parsed.inf = userData;

        localStorage.setItem(Constants.ACCREDITATION_USER_DATA_KEY, JSON.stringify(parsed));
      }
    }
  }

  public isBinServiceContractProposal(serviceContract: number): boolean {
    return this.serivceContractBinAndSipag.includes(serviceContract);
  }

  public isBinServiceContractProposalTecnology(serviceContract: number): boolean {
    return this.serivceContractBinAndSipagTecnology.includes(serviceContract);
  }

  public getUserDataSubChannel(userData: UserDataModel) {
    if (!userData.hierarchyComplementaryInformation || !userData.hierarchyComplementaryInformation.length) {
      return;
    }

    if (userData.hierarchyComplementaryInformation.length === 1) {
      return userData.hierarchyComplementaryInformation[0].subChannel;
    } else {
      const selected = userData.hierarchyComplementaryInformation.find((x) => !!x.selectedSubChannel);

      if (!selected) {
        return userData.hierarchyComplementaryInformation[0].subChannel;
      }

      return selected.subChannel;
    }
  }

  public getUserDataAgentChannel(userData: UserDataModel) {
    if (!userData.hierarchyComplementaryInformation || !userData.hierarchyComplementaryInformation.length) {
      return;
    }

    if (userData.hierarchyComplementaryInformation.length === 1) {
      return userData.hierarchyComplementaryInformation[0].agentId;
    } else {
      const selected = userData.hierarchyComplementaryInformation.find((x) => !!x.selectedAgentChannel);

      if (!selected) {
        return userData.hierarchyComplementaryInformation[0].agentId;
      }

      return selected.agentId;
    }
  }

  public getDraftDataFromLocalStorage() {
    const agentKey = this.getAgentCpfCnpjKey();

    const storedDraftData = localStorage.getItem(agentKey);
    if (storedDraftData) {
      const parsedDraftData = JSON.parse(storedDraftData) as LocalStorageData;

      if (!parsedDraftData) {
        return;
      }

      return parsedDraftData;
    }
  }

  public getAgentDataFromLocalStorage(draftUuid: string = null) {
    const draftData = this.getDraftDataFromLocalStorage();

    let activeDraft;
    if (draftUuid) {
      activeDraft = draftUuid;
    } else {
      activeDraft = localStorage.getItem(Constants.ACTIVE_DRAFT_SESSIONSTORAGE_KEY);
    }

    if (draftData) {
      return draftData[activeDraft] as LocalStorageAgentData;
    }
  }

  public clearAllLocalStorageObjects() {
    localStorage.clear();
  }

  public deleteDraftInformationFromLocalStorage(draftUuid: string) {
    const agentKey = this.getAgentCpfCnpjKey();

    const storedDraftData = localStorage.getItem(agentKey);
    if (storedDraftData) {
      const parsedDraftData = JSON.parse(storedDraftData) as LocalStorageData;

      if (!parsedDraftData) {
        return;
      }

      if (!draftUuid) {
        draftUuid = localStorage.getItem(Constants.ACTIVE_DRAFT_SESSIONSTORAGE_KEY);
      }

      delete parsedDraftData[draftUuid];

      localStorage.setItem(this.getAgentCpfCnpjKey(), JSON.stringify(parsedDraftData));
    }
  }

  public removeAgentFromLocalStorage() {
    localStorage.removeItem(this.getAgentCpfCnpjKey());
  }

  public setAgentDataOnLocalStorage(agentData: LocalStorageAgentData) {
    let storedData = this.getDraftDataFromLocalStorage();
    const activeDraftKey = localStorage.getItem(Constants.ACTIVE_DRAFT_SESSIONSTORAGE_KEY);

    if (!storedData) {
      storedData = {
        [activeDraftKey]: agentData,
      };
    } else {
      storedData[activeDraftKey] = merge(storedData[activeDraftKey], agentData);
    }

    localStorage.setItem(this.getAgentCpfCnpjKey(), JSON.stringify(storedData));
  }

  public setSessionInformation(sessionInfo: SessionInformation, tokenInfo: TokenInformationModel, isCEF: boolean) {
    localStorage.removeItem(Constants.ACCREDITATION_TOKEN_KEY);
    localStorage.removeItem(Constants.ACCREDITATION_USERNAME_KEY);
    localStorage.removeItem(Constants.ACCREDITATION_USER_DATA_KEY);
    localStorage.removeItem(Constants.IS_CEF_KEY);

    localStorage.setItem(Constants.ACCREDITATION_TOKEN_KEY, sessionInfo.sessionToken);
    localStorage.setItem(Constants.ACCREDITATION_USERNAME_KEY, sessionInfo.name);
    localStorage.setItem(Constants.ACCREDITATION_USER_DATA_KEY, JSON.stringify(tokenInfo));

    localStorage.setItem(Constants.IS_CEF_KEY, JSON.stringify(isCEF));
  }

  public getCEFFlag(): boolean {
    const storageItem = localStorage.getItem(Constants.IS_CEF_KEY);

    return storageItem && JSON.parse(storageItem);
  }

  public clearSessionInformation() {
    localStorage.clear();
  }

  public getLocalStorageObject(key: string, draftUuid: string = null): any {
    const parsedAgentData = this.getAgentDataFromLocalStorage(draftUuid);

    if (parsedAgentData) {
      const storageItem = parsedAgentData[key];
      if (!storageItem) {
        return null;
      }

      return storageItem;
    }
  }

  public getAgentCpfCnpjKey() {
    const userData = this.getUserData();

    if (userData) {
      return userData.cpfCnpj;
    }
  }

  public getUsername() {
    return localStorage.getItem(Constants.ACCREDITATION_USERNAME_KEY);
  }

  public getToken() {
    return localStorage.getItem(Constants.ACCREDITATION_TOKEN_KEY);
  }

  public getHierarchyComplementaryInformation() {
    return localStorage.getItem(Constants.HIERARCHY_COMPLEMENTARY_INFORMATION);
  }

  public getConfiguration() {
    return localStorage.getItem(Constants.CONFIGURATION_KEY);
  }
}
